import {
  startOfWeek,
  startOfMonth,
  addWeeks,
  addDays,
  addMonths,
  subDays,
  subWeeks,
  subMonths,
  format,
  isWithinInterval,
  isSameDay,
  isSameWeek,
  isSameMonth,
} from "date-fns";
import { type Payload } from "recharts/types/component/DefaultTooltipContent";
import { DEFAULT_INTERVAL_CONFIG, type IntervalType } from "./constants";
import { chartConfig, type bookingsChartQuery } from ".";
import { type ResultOf } from "@/graphql";
import { toJsDate } from "@/lib/utils/dates";

type RemapStatus<T> = T extends { status: infer S } ? Omit<T, "status"> & { status: Exclude<S, "CANCELLED"> } : T;

export type ChartBooking = NonNullable<
  NonNullable<NonNullable<ResultOf<typeof bookingsChartQuery>["supplyChainBookings"]>["edges"]>[number]
>["node"];

export type ChartBookingWithoutCancelledStatus = RemapStatus<ChartBooking>;
export type ChartBookingStatus = NonNullable<ChartBookingWithoutCancelledStatus>["status"];

type StatusCounts = {
  [K in ChartBookingStatus]: number;
};

export type ChartData = Array<{
  startDate: Date;
  label: string;
  countsByStatus: StatusCounts;
  totalCount: number;
}>;

/**
 * Creates an object containing all booking statuses initialized to zero count
 * @returns {StatusCounts} Object with all booking statuses set to 0
 */
export const createEmptyCounts = (): StatusCounts => {
  return {
    PLANNED: 0,
    DRAFT: 0,
    BOOKED: 0,
    SHIPPED: 0,
    AWAITING_PAYMENT: 0,
    PAID: 0,
    ARCHIVED: 0,
    CLOSED: 0,
  };
};

/**
 * Creates a new data bucket for a specific time period
 * @param {Date} start - The start date of the bucket
 * @returns {ChartData[number]} A bucket object containing metadata, empty counts for each booking status, and total
 */
const createBucket = (start: Date): ChartData[number] => {
  return {
    startDate: start,
    label: format(start, "MMM d"),
    countsByStatus: createEmptyCounts(),
    totalCount: 0,
  };
};

/**
 * Increments the count of a specific booking status in the provided chart data.
 *
 * @param data - An object representing a single data point in the chart, including counts by status and total count.
 * @param status - The booking status whose count needs to be incremented.
 * @param numberOfHead - The number to increment the count by. If null, it defaults to 0.
 * @returns A new chart data object with the updated counts for the specified status and total count.
 */
const incrementStatusCount = (
  data: ChartData[number],
  status: ChartBookingStatus,
  numberOfHead: number | null,
): ChartData[number] => {
  const headCount = numberOfHead ?? 0;

  return {
    ...data,
    countsByStatus: {
      ...data.countsByStatus,
      [status]: data.countsByStatus[status] + headCount,
    },
    totalCount: data.totalCount + headCount,
  };
};

export interface DateRange {
  start: Date;
  end: Date;
}

export interface AggregateParams {
  emptyBuckets: ChartData;
  bookings: Array<ChartBookingWithoutCancelledStatus>;
  dateRange: DateRange;
}

/**
 * Generates a date range spanning 4 weeks starting from the beginning of the current week
 * @param {Date} currentDate - The reference date to calculate the range from
 * @returns {DateRange} An object containing start and end dates of the range
 * @example
 * // If currentDate is "2024-01-17" (a Wednesday)
 * const range = getDateRange(new Date("2024-01-17"))
 * // Returns:
 * // {
 * //   start: "2024-01-15" (Monday of current week)
 * //   end: "2024-02-11" (Sunday of the 4th week, giving a complete 4-week span:
 * //                      Jan 15-21, Jan 22-28, Jan 29-Feb 4, Feb 5-11)
 * // }
 */
export const getDateRange = (currentDate: Date): DateRange => {
  const start = startOfWeek(currentDate, { weekStartsOn: 0 });
  // Add 4 weeks to get to the Monday of the 5th week, then subtract 1 day to get Sunday of the 4th week
  const end = addDays(addWeeks(start, 4), -1);
  return { start, end };
};

export interface DateRangeParams {
  referenceDate: Date;
  intervalType: IntervalType;
  bucketCount: number;
}

/**
 * Enhanced date range calculation that supports different intervals
 * @param params - Named parameters for date range calculation
 * @returns {DateRange} An object containing start and end dates of the range
 */
export const getDateRangeForInterval = ({ referenceDate, intervalType, bucketCount }: DateRangeParams): DateRange => {
  // Determine start date based on interval type
  const startDate =
    intervalType === "months" ? startOfMonth(referenceDate) : startOfWeek(referenceDate, { weekStartsOn: 0 });

  // Calculate end date based on interval type
  const endDate = (() => {
    switch (intervalType) {
      case "days":
        // For days, end date is bucketCount days after start (subtract 1 for inclusive range)
        return addDays(startDate, bucketCount - 1);
      case "weeks":
        // For weeks, end date is the last day of the last week
        return addDays(addWeeks(startDate, bucketCount), -1);
      case "months":
        // For months, end date is the last day of the last month
        return addDays(addMonths(startDate, bucketCount), -1);
      default:
        const _exhaustiveCheck: never = intervalType;
        throw new Error(`Unhandled interval type: ${_exhaustiveCheck}`);
    }
  })();

  return { start: startDate, end: endDate };
};

/**
 * Creates an array of empty weekly buckets for the chart data
 * @param {Object} params - The parameters for creating empty buckets
 * @param {Date} params.startDate - The start date for the first bucket
 * @param {number} params.numberOfWeeks - The number of weekly buckets to create
 * @returns {ChartData} Array of empty weekly buckets
 */
export const createEmptyWeeklyBuckets = ({
  startDate,
  numberOfWeeks,
}: {
  startDate: Date;
  numberOfWeeks: number;
}): ChartData => {
  return Array.from({ length: numberOfWeeks }, (_, i) => createBucket(addWeeks(startDate, i)));
};

export interface CreateEmptyBucketsParams {
  startDate: Date;
  intervalType: IntervalType;
  bucketCount: number;
}

/**
 * Creates an array of empty buckets for any interval type (days, weeks, months)
 * @param params - Named parameters for bucket creation
 * @returns Array of empty buckets with correct date ranges and labels
 */
export const createEmptyBuckets = ({ startDate, intervalType, bucketCount }: CreateEmptyBucketsParams): ChartData => {
  return Array.from({ length: bucketCount }, (_, i) => {
    const bucketStartDate = (() => {
      switch (intervalType) {
        case "days":
          return addDays(startDate, i);
        case "weeks":
          return addWeeks(startDate, i);
        case "months":
          return addMonths(startDate, i);
        default:
          const _exhaustiveCheck: never = intervalType;
          throw new Error(`Unhandled interval type: ${_exhaustiveCheck}`);
      }
    })();

    const label = (() => {
      switch (intervalType) {
        case "days":
          // Format: "Mon 10"
          return format(bucketStartDate, "EEE d");
        case "weeks":
          // Format: "Jun 10"
          return format(bucketStartDate, "MMM d");
        case "months":
          // Format: "June"
          return format(bucketStartDate, "MMMM");
        default:
          const _exhaustiveCheck: never = intervalType;
          throw new Error(`Unhandled interval type: ${_exhaustiveCheck}`);
      }
    })();

    return {
      startDate: bucketStartDate,
      label,
      countsByStatus: createEmptyCounts(),
      totalCount: 0,
    };
  });
};

export interface GetBucketForDateParams {
  date: Date;
  buckets: ChartData;
  intervalType: IntervalType;
}

/**
 * Finds the bucket that a given date belongs to based on the interval type
 * @param params - Named parameters for bucket lookup
 * @returns The matching bucket or undefined if no match found
 */
export const getBucketForDate = ({
  date,
  buckets,
  intervalType,
}: GetBucketForDateParams): ChartData[number] | undefined => {
  return buckets.find((bucket) => {
    switch (intervalType) {
      case "days":
        return isSameDay(date, bucket.startDate);
      case "weeks":
        return isSameWeek(date, bucket.startDate, { weekStartsOn: 0 });
      case "months":
        return isSameMonth(date, bucket.startDate);
      default:
        const _exhaustiveCheck: never = intervalType;
        throw new Error(`Unhandled interval type: ${_exhaustiveCheck}`);
    }
  });
};

export interface AggregateBookingsForIntervalParams {
  buckets: ChartData;
  bookings: Array<ChartBookingWithoutCancelledStatus>;
  intervalType: IntervalType;
}

/**
 * Aggregates bookings into buckets based on the specified interval type
 * @param params - Named parameters for aggregation
 * @returns New array of buckets with aggregated booking data
 */
export const aggregateBookingsIntoBucketsForInterval = ({
  buckets,
  bookings,
  intervalType,
}: AggregateBookingsForIntervalParams): ChartData => {
  const filledBuckets = buckets.map((bucket) => ({
    ...bucket,
    countsByStatus: { ...bucket.countsByStatus },
  }));

  const result = bookings.reduce((currentBuckets, booking) => {
    if (!booking?.plannedShippingDate || !booking.status) {
      return currentBuckets;
    }

    const bookingDate = toJsDate(booking.plannedShippingDate);
    if (!bookingDate) {
      return currentBuckets;
    }

    // Find target bucket
    const targetBucket = getBucketForDate({
      date: bookingDate,
      buckets: currentBuckets,
      intervalType,
    });

    if (!targetBucket) {
      return currentBuckets;
    }

    const headCount = booking.numberOfHead ?? 0;

    return currentBuckets.map((bucket) =>
      bucket === targetBucket
        ? {
            ...bucket,
            totalCount: bucket.totalCount + headCount,
            countsByStatus: {
              ...bucket.countsByStatus,
              [booking.status]: (bucket.countsByStatus[booking.status] || 0) + headCount,
            },
          }
        : bucket,
    );
  }, filledBuckets);

  return result;
};

interface ClassifyBookingParams {
  booking: ChartBookingWithoutCancelledStatus;
  dateRange: DateRange;
}

/**
 * Gets the Monday date of the week containing the booking's planned shipping date
 * @param {ClassifyBookingParams} params - The parameters
 * @param {ChartBookingWithoutCancelledStatus} params.booking - The booking to get the week start date for
 * @param {DateRange} params.dateRange - The date range to check against
 * @returns {Date | null} The Monday date for the booking's week, or null if invalid/out of range
 */
export const getBookingWeekStartDate = ({ booking, dateRange }: ClassifyBookingParams): Date | null => {
  if (!booking?.plannedShippingDate) {
    return null;
  }

  const bookingDate = toJsDate(booking.plannedShippingDate);
  if (!bookingDate) {
    return null;
  }

  if (!isWithinInterval(bookingDate, dateRange)) {
    return null;
  }

  return startOfWeek(bookingDate, { weekStartsOn: 0 });
};

/**
 * Aggregates bookings into weekly buckets based on their planned shipping dates
 * @param {AggregateParams} params - The parameters for aggregation
 * @param {ChartData} params.emptyBuckets - Pre-initialized array of empty weekly buckets
 * @param {Array<ChartBookingWithoutCancelledStatus>} params.bookings - Array of bookings to be aggregated
 * @param {DateRange} params.dateRange - The date range to consider for aggregation
 * @returns {ChartData} Array of weekly buckets with aggregated booking counts
 */
export const aggregateBookingsIntoBuckets = ({ emptyBuckets, bookings, dateRange }: AggregateParams): ChartData => {
  return bookings.reduce((buckets, booking) => {
    if (!booking) {
      return buckets;
    }

    const weekStartDate = getBookingWeekStartDate({ booking, dateRange });

    if (!weekStartDate || !booking.status) {
      return buckets;
    }

    const chartData = buckets.map((bucket) => {
      return bucket.startDate.getTime() === weekStartDate.getTime()
        ? incrementStatusCount(bucket, booking.status, booking.numberOfHead)
        : bucket;
    });
    return chartData;
  }, emptyBuckets);
};

/**
 * Transforms chart payload data into a format suitable for tooltip display
 * @param payloads - The raw payload data from Recharts
 * @returns Object containing total animals count and breakdown by status
 */
export const calculateTooltipData = (payloads: Payload<number, string>[] | undefined) => {
  if (!payloads) {
    return { countTotalAnimals: 0, statusBreakdowns: undefined };
  }

  const countTotalAnimals = payloads?.reduce((acc, payload) => {
    if (typeof payload === "string") return acc;
    return acc + (payload.value || 0);
  }, 0);

  const statusBreakdowns = payloads
    ?.map((payload) => {
      const status = payload.name?.replace("countsByStatus.", "");
      if (!status || !isBookingStatus(status)) return undefined;

      return {
        status,
        color: chartConfig[status].color,
        label: chartConfig[status].label,
        countAnimals: payload.value,
      };
    })
    .filter((breakdown): breakdown is NonNullable<typeof breakdown> => Boolean(breakdown))
    .sort((breakdownA, breakdownB) => {
      return breakdownA.label.localeCompare(breakdownB.label);
    });

  return { countTotalAnimals, statusBreakdowns };
};

/**
 * Determines if a specific bucket (time period) is selected based on date range parameters
 * @param bucket - The data bucket representing a specific time period
 * @param dateOpenedAfter - The start date of the selection range in 'yyyy-MM-dd' format
 * @param dateOpenedBefore - The end date of the selection range in 'yyyy-MM-dd' format
 * @returns A boolean - True if the bucket falls within the selected date range, false otherwise
 */
export const isBucketSelected = ({
  bucket,
  dateOpenedAfter,
  dateOpenedBefore,
}: {
  bucket: ChartData[number];
  dateOpenedAfter: string | undefined;
  dateOpenedBefore: string | undefined;
}): boolean => {
  if (!dateOpenedAfter || !dateOpenedBefore) return false;

  return (
    dateOpenedAfter === format(bucket.startDate, "yyyy-MM-dd") &&
    dateOpenedBefore === format(addDays(addWeeks(bucket.startDate, 1), -1), "yyyy-MM-dd")
  );
};

const isBookingStatus = (status: string): status is ChartBookingStatus => {
  return status in chartConfig;
};

export const isChartBooking = (
  booking: NonNullable<ChartBooking>,
): booking is NonNullable<ChartBookingWithoutCancelledStatus> => {
  return booking && booking.status !== "CANCELLED";
};

/**
 * Calculates the end date for a given interval based on the start date and interval type
 * @param startDate - The start date of the interval
 * @param intervalType - The type of interval (days, weeks, months)
 * @returns ISO date string representing the end of the interval
 */
export const calculateEndDateForInterval = (startDate: Date, intervalType: IntervalType): string => {
  switch (intervalType) {
    case "days":
      return format(startDate, "yyyy-MM-dd"); // Same day
    case "weeks":
      return format(addDays(addWeeks(startDate, 1), -1), "yyyy-MM-dd");
    case "months":
      return format(addDays(addMonths(startDate, 1), -1), "yyyy-MM-dd");
    default:
      const _exhaustiveCheck: never = intervalType;
      throw new Error(`Unhandled interval type: ${_exhaustiveCheck}`);
  }
};

/**
 * Gets the bucket count for a given interval type from the default configuration
 * @param interval - The interval type to get bucket count for
 * @returns Number of buckets to display for the given interval
 */
export const getBucketCountForInterval = (interval: IntervalType): number => {
  return DEFAULT_INTERVAL_CONFIG.bucketCount[interval];
};

/**
 * Calculates how many units to step when navigating (e.g., 7 days, 4 weeks, 3 months)
 */
export const getNavigationStep = (intervalType: IntervalType): number => {
  const bucketCount = DEFAULT_INTERVAL_CONFIG.bucketCount[intervalType];
  return bucketCount;
};

/**
 * Calculates the next reference date when navigating to the previous period
 */
export const calculatePreviousDate = (referenceDate: Date, intervalType: IntervalType): Date => {
  const step = getNavigationStep(intervalType);

  switch (intervalType) {
    case "days":
      return subDays(referenceDate, step);
    case "weeks":
      return subWeeks(referenceDate, step);
    case "months":
      return subMonths(referenceDate, step);
    default:
      const _exhaustiveCheck: never = intervalType;
      throw new Error(`Unhandled interval type: ${_exhaustiveCheck}`);
  }
};

/**
 * Calculates the next reference date when navigating to the next period
 */
export const calculateNextDate = (referenceDate: Date, intervalType: IntervalType): Date => {
  const step = getNavigationStep(intervalType);

  switch (intervalType) {
    case "days":
      return addDays(referenceDate, step);
    case "weeks":
      return addWeeks(referenceDate, step);
    case "months":
      return addMonths(referenceDate, step);
    default:
      const _exhaustiveCheck: never = intervalType;
      throw new Error(`Unhandled interval type: ${_exhaustiveCheck}`);
  }
};
